import { Injectable, Component } from '@angular/core';
import { Observable, throwError, NEVER, from } from 'rxjs';
import { DialogService } from "./DialogService";
import { ConfigService } from "./ConfigService";
import { HttpClient } from '@angular/common/http';
import { catchError, tap, map, debounceTime } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import { BlobResponse } from '../Models/OtherClasses';

@Injectable()
export class BaseService {

	constructor(private _http: HttpClient,
		private modalService: DialogService,
		public configData: ConfigService) { }

	getMyHeaders() {
		var xsrf = this.configData.getCookie('EPMXSRFFORM', true);
		var headers: HttpHeaders = new HttpHeaders({
			'companyid': this.configData.AppConfig.company,
			'Content-Type': 'application/json',
			'If-Modified-Since': 'Mon, 01 Jan 1990 00:00:00 GMT',
			'Cache-Control': 'no-cache',
			'Pragma': 'no-cache',
			'EPMXSRF': xsrf
		}); // ... Set content type to JSON

		return headers;
	}



	showError(msg: string) {
		return (error: any): Observable<never> => {
			if (error) {
				if (error.status == 402) {
					return from(NEVER);
				}
				var myErrorObj = error.error;

				if ((error.status != 400 && error.status != 404) || (myErrorObj && !myErrorObj.ModelState)) {
					if (myErrorObj && myErrorObj.Message) {
						if (myErrorObj.Message.indexOf("Failed:") >= 0) {
							//don't do anything error as this should be handled by the model state displays
						} else {
							let bodyMsg: string = "";
							if (myErrorObj.ExceptionMessage) {
								bodyMsg += myErrorObj.ExceptionMessage;
							}
							if (myErrorObj.StackTrace) {
								bodyMsg += "<br />" + myErrorObj.StackTrace;
							}
							if (bodyMsg) {
								bodyMsg = this.configData.TranslateWords.IfThisContinuesSendDevelopers +
									' : <br /><br /><br />' + bodyMsg;
							}
							this.modalService.error(myErrorObj.Message, bodyMsg);
						}
					} else {
						var cache: any[] = [];

						this.modalService.error(msg, this.configData.TranslateWords.IfThisContinuesSendDevelopers +
							' : <br /><br /><br />' + JSON.stringify(error, function (key, value) {
								if (typeof value === 'object' && value !== null) {
									if (cache.indexOf(value) !== -1) {
										// Circular reference found, discard key
										return;
									}
									// Store value in our collection
									cache.push(value);
								}
								return value;
							}));
						cache = null; // Enable garbage collection
					}
				}
			} else {
				this.modalService.error(msg, '');
			}
			return this.throwError(error);
		}
	}

	throwError(error: any): Observable<never> {
		if (error) {
			return throwError(error);
		} else {
			return throwError('Server error');
		}
	}

	loadAll(baseUrl: string): Observable<any> {
		return this.getPlain(baseUrl, this.configData.TranslateWords.FailedToLoadItems);
	}

	getEdit(id: System.Guid, baseUrl: string): Observable<any> {
		return this.get(id, baseUrl + '/Edit');
	}

	get(id: System.Guid, baseUrl: string): Observable<any> {
		return this.getWithParams(id, baseUrl, "");
	}

	getWithParams(id: System.Guid, baseUrl: string, parameters: string): Observable<any> {
		id = id || this.configData.DefaultEmptyGuid;
		return this.rawWithParams(id, baseUrl, parameters);
	}

	getPlain(url: string, errorMessage: string): Observable<any> {
		return this._http.get<any>(url, { observe: "response", headers: this.getMyHeaders() })
			.pipe(
				tap((res) => {
					if (res && res.headers) {
						var version = res.headers.get("serverVersion");
						if (version && this.configData.shouldShowVersionAlert(version)) {
							this.modalService.message(this.configData.TranslateWords.NewVersion, 'Version');
						}
						var isPaid = res.headers.get("ePMPaidStatus");
						if (isPaid && this.configData.shouldShowMoneyAlert(isPaid)) {
							//this.modalService.message(this.configData.TranslateWords.NewVersion, 'Version');
						}
					}
				}),
				map(res => {
					return res.body;
				}),
				catchError(this.showError(errorMessage))
			);
	}

	rawWithParams(id: System.Guid, baseUrl: string, parameters: string): Observable<any> {
		return this.getPlain(baseUrl + '/' + id + parameters, this.configData.TranslateWords.FailedToReload);
	}

	save(tempItem: ECI.Basic.ObjectBase, baseUrl: string): Observable<any> {
		let bodyString = JSON.stringify(tempItem); // Stringify payload
		let options = { headers: this.getMyHeaders() }; // Create a request option

		return this._http.post(baseUrl, bodyString, options)
			.pipe(
				catchError(this.showError(this.configData.TranslateWords.Failed))
			);
	}

	returnBlob(tempItem: any, baseUrl: string): Observable<BlobResponse> {
		let bodyString = JSON.stringify(tempItem); // Stringify payload

		return this._http.post(baseUrl, bodyString, { headers: this.getMyHeaders(), responseType: "blob", observe: "response" })
			.pipe(
				map(res => {
					var data = {
						blob: new Blob([res.body], { type: res.headers.get('Content-Type') }),
						fileName: this.getFileName(res.headers)
					} as BlobResponse;

					return data;
				}),
				debounceTime(200),
				catchError(this.showError(this.configData.TranslateWords.Failed))
			);
	}
	loadBlob(baseUrl: string): Observable<BlobResponse> {
		return this._http.get(baseUrl, { headers: this.getMyHeaders(), responseType: "blob", observe: "response" })
			.pipe(
				map(res => {
					var data = {
						blob: new Blob([res.body], { type: res.headers.get('Content-Type') }),
						fileName: this.getFileName(res.headers)
					} as BlobResponse;

					return data;
				}),
				debounceTime(200),
				catchError(this.showError(this.configData.TranslateWords.Failed))
			);
	}
	getFileName(headers: HttpHeaders): string {
		var name = headers.get('File-Name');
		if (!name) {
			name = headers.get('content-disposition');
			if (name) {
				name = name.substring(name.indexOf("filename=")).replace("filename=", "");
			}
		}
		return name;
	}
	post(tempItem: any, baseUrl: string): Observable<any> {
		let bodyString = JSON.stringify(tempItem); // Stringify payload
		let options = { headers: this.getMyHeaders(), requestProgress: true }; // Create a request option

		return this._http.post(baseUrl, bodyString, options)
			.pipe(
				catchError(this.showError(this.configData.TranslateWords.Failed))
			);
	}

	deleteItem(id: System.Guid, baseUrl: string): Observable<any> {
		return this.deleteWithParams(id, baseUrl, "");
	}

	deleteWithParams(id: System.Guid, baseUrl: string, parameters: string): Observable<any> {
		return this._http.delete(baseUrl + '/' + id + parameters, { headers: this.getMyHeaders() })
			.pipe(
				catchError(this.showError(this.configData.TranslateWords.Failed))
			);
	}
}